export default function ComparisonChecklist() {
  const questions = [
    {
      question: 'Are you licensed and insured for remodeling work in Texas?',
      why: 'Texas requires contractors to carry proper licensing and insurance. Verify their credentials and ensure they have both general liability insurance and workers\' compensation. This protects you from liability if accidents occur during your remodeling project.',
      redFlag: 'Contractors who can\'t provide proof of insurance or licensing should be avoided immediately.'
    },
    {
      question: 'How many years have you been remodeling homes in El Paso?',
      why: 'Local experience matters for understanding El Paso building codes, climate considerations, and architectural styles. Experienced contractors know which materials perform best in our desert environment and have established relationships with quality suppliers and subcontractors.',
      redFlag: 'Be cautious of contractors new to the area who may not understand local requirements and challenges.'
    },
    {
      question: 'Can you provide references and photos of recent similar projects?',
      why: 'Recent references demonstrate current work quality and customer satisfaction. Ask to see completed projects similar in scope to yours. Contact references directly to ask about communication, timeline adherence, and overall satisfaction with the finished work.',
      redFlag: 'Contractors who can\'t provide recent local references or portfolio photos may lack experience or satisfied customers.'
    },
    {
      question: 'Will you create a detailed project plan and timeline?',
      why: 'Professional contractors provide comprehensive plans including design drawings, material specifications, work schedule, and milestone dates. Clear timelines help you plan around the disruption and hold contractors accountable for progress.',
      redFlag: 'Vague timelines or reluctance to commit to schedules often indicate poor project management.'
    },
    {
      question: 'Who will be working on my project daily?',
      why: 'Understanding who will be in your home is important for security and quality. Ask about subcontractors, their qualifications, and supervision. Quality contractors use trusted teams and maintain consistent site supervision throughout the project.',
      redFlag: 'Contractors who constantly change crews or can\'t identify their workers raise concerns about quality control.'
    },
    {
      question: 'How do you handle permits and inspections?',
      why: 'Most remodeling projects require building permits from the City of El Paso. Professional contractors handle all permitting, schedule required inspections, and ensure work meets code. Permits protect your investment and home value.',
      redFlag: 'Never work with contractors who suggest skipping permits to save money—this is illegal and risky.'
    },
    {
      question: 'What is your payment schedule?',
      why: 'Reasonable payment schedules tie payments to project milestones, not arbitrary dates. Typical structure: 10-20% deposit, progress payments at completion of major phases, and final payment upon your satisfaction and final inspection approval.',
      redFlag: 'Never pay the full amount upfront. Large deposits or demands for cash payments are major red flags.'
    },
    {
      question: 'How will you protect my home during construction?',
      why: 'Remodeling is messy and disruptive. Professional contractors use dust barriers, floor protection, and proper ventilation. They should explain their cleanup procedures, debris removal schedule, and how they\'ll minimize impact on areas not being remodeled.',
      redFlag: 'Contractors who don\'t discuss property protection may leave you with damage beyond the work area.'
    },
    {
      question: 'What warranties do you offer on your work?',
      why: 'Quality contractors stand behind their work with written warranties covering workmanship for at least 1-2 years. Material warranties come from manufacturers. Get all warranty terms in writing, including what\'s covered, duration, and claim procedures.',
      redFlag: 'Contractors offering no warranty or only verbal promises lack confidence in their work quality.'
    },
    {
      question: 'How do you handle changes and unexpected issues?',
      why: 'Remodeling often reveals hidden problems like outdated wiring or water damage. Professional contractors explain how they handle change orders, additional costs, and timeline adjustments. Clear communication prevents disputes and surprise expenses.',
      redFlag: 'Contractors who won\'t discuss potential issues or change order procedures may surprise you with unexpected costs.'
    }
  ];

  return (
    <section id="checklist" className="py-20 px-6 bg-gray-50">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            10 Questions to Ask Your El Paso Remodeling Contractor
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Use this comprehensive checklist when interviewing remodeling contractors. These critical questions help you identify qualified professionals and avoid costly mistakes. Print this list and bring it to every consultation.
          </p>
        </div>

        <div className="bg-white rounded-xl border-2 border-teal-600 p-8 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
              <i className="ri-lightbulb-line text-2xl text-teal-600"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">How to Use This Checklist</h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                Schedule consultations with at least 3 contractors. Ask each question and take detailed notes on their responses. Pay attention to their communication style and professionalism. Compare responses to identify the most qualified contractor for your remodeling project.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {questions.map((item, index) => (
            <div key={index} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
              <div className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-600 text-white font-bold rounded-lg flex-shrink-0">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-3">{item.question}</h3>
                    
                    <div className="space-y-3">
                      <div className="bg-blue-50 rounded-lg p-4">
                        <p className="text-xs font-semibold text-blue-900 mb-1 flex items-center gap-2">
                          <i className="ri-information-line"></i>
                          Why This Matters
                        </p>
                        <p className="text-sm text-blue-900 leading-relaxed">{item.why}</p>
                      </div>
                      
                      <div className="bg-red-50 rounded-lg p-4">
                        <p className="text-xs font-semibold text-red-900 mb-1 flex items-center gap-2">
                          <i className="ri-alert-line"></i>
                          Red Flag Warning
                        </p>
                        <p className="text-sm text-red-900 leading-relaxed">{item.redFlag}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl p-8">
          <h3 className="text-2xl font-bold text-white mb-4 text-center">
            Additional Tips for Hiring Remodeling Contractors
          </h3>
          <div className="grid md:grid-cols-2 gap-6 text-sm text-white/90">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-teal-400 text-xl flex-shrink-0 mt-0.5"></i>
                <p><strong className="text-white">Get Multiple Bids:</strong> Obtain at least 3 detailed written estimates to compare pricing, materials, and approaches. The lowest bid isn\'t always the best value.</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-teal-400 text-xl flex-shrink-0 mt-0.5"></i>
                <p><strong className="text-white">Check Online Reviews:</strong> Research contractors on Google, BBB, and Yelp for consistent positive feedback. Look for how they respond to complaints.</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-teal-400 text-xl flex-shrink-0 mt-0.5"></i>
                <p><strong className="text-white">Review Contracts Carefully:</strong> Ensure everything discussed is in writing, including materials, timeline, payment schedule, and warranty terms.</p>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-teal-400 text-xl flex-shrink-0 mt-0.5"></i>
                <p><strong className="text-white">Visit Active Job Sites:</strong> Ask to see current projects to observe work quality, site cleanliness, and professionalism.</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-teal-400 text-xl flex-shrink-0 mt-0.5"></i>
                <p><strong className="text-white">Trust Your Instincts:</strong> If something feels off or a contractor pressures you to sign immediately, walk away. Quality contractors respect your decision-making process.</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-teal-400 text-xl flex-shrink-0 mt-0.5"></i>
                <p><strong className="text-white">Document Everything:</strong> Take photos before, during, and after the project. Keep all receipts, permits, and warranty documents organized.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
